import { $fetch } from 'ofetch'
import { jwtDecode } from 'jwt-decode'
import { v4 as uuidv4 } from 'uuid'
import {
  JWT_ACCESS_TOKEN,
  ONE_YEAR_DURATION,
  REFRESH_TOKEN,
} from '~/src/constants/common'
import { logError } from '~/utils/log-error'
import type { TCommon } from '~/src/types/common'

/**
 * Adds an Authorization header to the option object if a token is provided
 *
 * @param options - The options object to add the header to
 * @param token - The JWT token to add to the header
 * @returns The options object with the Authorization header added (if a token was provided)
 */
const addAuthorizationHeader = (options: TCommon, token?: string) => {
  // If no token is provided, return the options unchanged
  if (!token) return options

  // Add the Authorization header with the Bearer token
  options.headers.Authorization = `Bearer ${token}`

  // Return the updated options
  return options
}

export const decodeToken = (token?: string) => {
  // Initialize decoded token as null
  let decoded = null

  // Only attempt to decode if a token is provided
  if (token) {
    try {
      // Attempt to decode the token
      decoded = jwtDecode(token)
    } catch (error) {
      // Log any errors that occur during decoding
      logError(error)
      // Keep decoded as null if decoding fails
    }
  }

  // Return the decoded token or null if decoding failed
  return decoded
}

export const isTokenValid = ($cookies: TCommon) => {
  // Get the JWT token from cookies
  const jwtToken = $cookies.get(JWT_ACCESS_TOKEN)

  // If no token is available, or it's empty, it's not valid
  if (!jwtToken || jwtToken === '') return false

  // Try to decode the token
  const decodedToken = decodeToken(jwtToken)

  // If the token can't be decoded, it's not valid
  if (!decodedToken) return false

  // Check if the token has expired
  // Token is valid if the current time is less than the expiration time
  return Date.now() < decodedToken.exp * 1000
}

export const getNewToken = async ({ $cookies, $config }: TCommon) => {
  // Get the refresh token from cookies
  const refreshToken = $cookies.get(REFRESH_TOKEN)

  // If no refresh token is available, we can't get a new token
  if (!refreshToken || refreshToken === '') return null

  try {
    // Make the API call to refresh the token
    const fetchResult = await $fetch($config.public.graphqlEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `
          mutation refreshToken {
            refreshJwtAuthToken(input: { clientMutationId: "${uuidv4()}", jwtRefreshToken: "${refreshToken}" }) {
              authToken
            }
          }
        `,
      }),
    })

    // Check if the response has the expected structure
    if (!fetchResult?.data?.refreshJwtAuthToken?.authToken) {
      return null
    }

    // Extract the new auth token
    const authToken = fetchResult.data.refreshJwtAuthToken.authToken

    // Store the new token in cookies
    $cookies.set(JWT_ACCESS_TOKEN, authToken, {
      expires: ONE_YEAR_DURATION,
      path: '/',
    })

    // Return the new token
    return authToken
  } catch (error) {
    // Log any errors that occur during the token refresh
    logError(error)
    return null
  }
}

export const getToken = async ({ $cookies, $config }: TCommon) => {
  // First, check if the current token is valid
  if (isTokenValid($cookies)) {
    return $cookies.get(JWT_ACCESS_TOKEN)
  }

  // If not valid, try to get a new token
  // Return the new token if available, otherwise return null
  return getNewToken({ $cookies, $config })
}

/**
 * Add an Authorization header if it is necessary
 *
 * @param {*} options
 * @param $cookies
 * @param $config
 * @returns
 */
export const extendOptions = async (
  options: TCommon,
  $cookies: TCommon,
  $config: TCommon,
) => {
  // Get the token (either an existing valid token or a new one)
  const jwtToken = await getToken({ $cookies, $config })

  // Add the authorization header if a token is available
  return addAuthorizationHeader(options, jwtToken)
}
